import { EmployeeShiftSwap } from '../models/employee-shift-swap';

/**
 * Welche meiner Tauschanfragen gehören unter „Das erwartet dich heute" — und welche nicht.
 *
 * ## Warum es diese Funktion gibt
 *
 * Die Kachel las ihre Einträge aus `GET /ShiftSwap/GetHistory` — einer **Historie**. Der
 * Mapper verwarf dabei `Status`, `RequestedAt`, `ExpiresAt` und `IsExpired`, obwohl alle vier
 * im Server-DTO stehen. Die Oberfläche konnte einen offenen Vorgang deshalb gar nicht von
 * einem abgeschlossenen unterscheiden.
 *
 * **Am laufenden System gemessen (19.09.2026, Mitarbeiter 44): alle fünf Einträge der Kachel
 * waren erledigt** — vier `Rejected`, einer `AcceptedFromAdmin`, der jüngste vom 10.09. Unter
 * der Überschrift „Das erwartet dich heute" stand also fünfmal etwas, das niemanden mehr
 * erwartet. Der Befund nannte das „zeigt Vergangenheit"; das Datum ist aber nur das Symptom,
 * der abgeschlossene Zustand die Ursache.
 *
 * ## ⚠️ `IsExpired` ist keine verlässliche Quelle
 *
 * Dieselbe Messung: alle fünf Einträge tragen `IsExpired: false`, obwohl ihr `ExpiresAt` neun
 * Tage zurückliegt. Das Feld wird offenbar nur im laufenden Vorgang fortgeschrieben.
 * **Maßgeblich ist der `Status`**, ergänzt um den Vergleich der Zeitpunkte mit *jetzt*.
 */

/** `Domain/Entities/Transaction/Common.cs` → `ShiftSwapStatus`. */
export enum SwapStatus {
  Created = 1,
  AcceptedFromEmployee = 2,
  AcceptedFromAdmin = 3,
  Rejected = 4,
  Closed = 5,
  Cancelled = 6,
  Expired = 7,
  PendingManagerApproval = 8,
}

/**
 * Zustände, in denen der Vorgang noch auf jemanden wartet.
 *
 * `AcceptedFromEmployee` gehört **nicht** dazu: dort hat ein Kollege zugesagt, der Vorgang
 * läuft weiter, aber er wartet nicht mehr auf den Anfragenden. Wer ihn als offen führt, lässt
 * eine Kachel stehen, an der es nichts zu tun gibt.
 */
const OFFENE_ZUSTAENDE: ReadonlySet<number> = new Set([
  SwapStatus.Created,
  SwapStatus.PendingManagerApproval,
]);

export interface SwapRequestGroups {
  /** Offen und noch rechtzeitig — gehört unter „heute". */
  offen: EmployeeShiftSwap[];
  /**
   * Offen, aber die Frist oder die Schicht ist vorbei. Gehört **nicht** weggefiltert:
   * eine zwei Wochen alte unbeantwortete Anfrage ist eine Aufgabe, keine Altlast.
   */
  ueberfaellig: EmployeeShiftSwap[];
  /** Abgeschlossen — erscheint in der Kachel gar nicht mehr. */
  erledigt: EmployeeShiftSwap[];
}

function zeitpunkt(wert: unknown): number | null {
  if (!wert) return null;
  const t = new Date(wert as string).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Ist der offene Vorgang über seine Zeit hinaus?
 *
 * Zwei Gründe, und beide zählen: die **Frist** (`ExpiresAt`) und der **Schichtbeginn**.
 * Ohne den zweiten stünde eine Anfrage für eine längst gelaufene Schicht noch als
 * fristgerecht da, wenn der Server keine Frist gesetzt hat.
 */
function istUeberfaellig(eintrag: EmployeeShiftSwap, jetzt: number): boolean {
  const frist = zeitpunkt((eintrag as { ExpiresAt?: unknown }).ExpiresAt);
  if (frist !== null && frist < jetzt) return true;
  const beginn = zeitpunkt(eintrag.StartTime);
  return beginn !== null && beginn < jetzt;
}

/**
 * Teilt meine Tauschanfragen in die drei Lagen auf.
 *
 * ⚠️ **Ein Eintrag ohne `Status` gilt als offen.** Ältere Server-Antworten und der
 * Zwischenstand vor dem Mapper-Umbau tragen das Feld nicht; ihn stillschweigend als erledigt
 * zu behandeln, würde eine echte Anfrage verschwinden lassen. Sichtbar zu viel ist hier
 * besser als unsichtbar zu wenig.
 */
export function swapRequestGroups(
  items: readonly EmployeeShiftSwap[] | null | undefined,
  jetzt: Date = new Date(),
): SwapRequestGroups {
  const gruppen: SwapRequestGroups = { offen: [], ueberfaellig: [], erledigt: [] };
  const t = jetzt.getTime();

  for (const eintrag of items ?? []) {
    const status = (eintrag as { Status?: number | null }).Status;
    const istOffen = status === null || status === undefined || OFFENE_ZUSTAENDE.has(status);

    if (!istOffen) {
      gruppen.erledigt.push(eintrag);
    } else if (istUeberfaellig(eintrag, t)) {
      gruppen.ueberfaellig.push(eintrag);
    } else {
      gruppen.offen.push(eintrag);
    }
  }

  return gruppen;
}

/**
 * Wie viele volle Tage liegt die Anfrage zurück — für die Beschriftung „offen seit N Tagen".
 *
 * Gerechnet auf **Kalendertagen**, nicht auf 24-Stunden-Blöcken: „gestern gestellt" soll
 * „seit 1 Tag" heißen, auch wenn erst 14 Stunden vergangen sind.
 */
export function offenSeitTagen(
  eintrag: EmployeeShiftSwap,
  jetzt: Date = new Date(),
): number | null {
  const gestellt = zeitpunkt((eintrag as { RequestedAt?: unknown }).RequestedAt);
  if (gestellt === null) return null;
  const a = new Date(gestellt);
  const tagA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const tagB = Date.UTC(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  const tage = Math.floor((tagB - tagA) / 86_400_000);
  return tage > 0 ? tage : null;
}
